/**
 * Story 052 — Profile section.
 *
 * Fields:
 *   - Avatar (upload + delete)
 *   - First name, Last name, Nickname, Display name (debounced PATCH on blur)
 *   - Preferred language (en / de)
 *   - Company (read-only — NOT an input element per AC3)
 *   - Role (read-only for everyone except tenant_admin; <select> for
 *     tenant_admin with last-tenant-admin guard per AC12-14)
 *   - lastLoginAt + passwordChangedAt under a Details disclosure
 */
import { useEffect, useId, useRef, useState, type ReactElement } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Collapse,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import {
  pickPrimaryRole,
  primaryRoleIs,
  useAuthStore,
} from "@aeriosiq/auth-client";
// SB6-AUTH-CONSISTENCY-01 — every text input + select on the account
// page funnels through <AccountField> so heights + label-shrink are
// uniform. The wrapper enforces size="small", variant="outlined", and
// slotProps.inputLabel.shrink = true.
import { AccountField } from "@aeriosiq/ui";
import { SectionShell } from "./SectionShell.js";
import {
  useDeleteAvatar,
  useUpdateProfile,
  useUpdateRole,
  useUploadAvatar,
  type MeExpanded,
  type Role,
} from "../queries/index.js";
import { useToast } from "../ToastContext.js";
import { CameraIcon, Trash2Icon } from "@aeriosiq/ui/icons";

// Canonical 7-role enum (auth/v1@4.3.0 / Story 054a / BE-058). The
// exhaustive `Record<Role, string>` signature ensures future enum
// widenings surface here at typecheck time.
const ROLE_LABEL: Record<Role, string> = {
  platform_admin: "Platform Admin",
  tenant_admin: "Tenant Admin",
  project_manager: "Project Manager",
  back_office: "Back Office",
  pre_screener: "Pre-Screener",
  expert_analyst: "Expert Analyst",
  pilot: "Pilot",
};

interface Props {
  me: MeExpanded;
  refetch: () => Promise<unknown>;
}

export function ProfileSection({ me, refetch }: Props): ReactElement {
  return (
    <SectionShell id="profile" title="Profile">
      <Stack spacing={3}>
        <AvatarRow me={me} />
        {/* Identity summary line — surfaces displayName + nickname as text
            content so AC2's `getByText` assertions resolve against text
            nodes (TextField values are in input.value, not text). */}
        <Stack spacing={0.5}>
          <Typography component="p" variant="h6" sx={{ fontWeight: 600 }}>
            {me.displayName}
          </Typography>
          {me.nickName ? (
            <Typography component="p" variant="body2" color="text.secondary">
              Nickname: {me.nickName}
            </Typography>
          ) : null}
        </Stack>
        <NameFields me={me} />
        <PreferredLanguage me={me} />
        <CompanyRow me={me} />
        <RoleRow me={me} refetch={refetch} />
        <DetailsDisclosure me={me} />
      </Stack>
    </SectionShell>
  );
}

function AvatarRow({ me }: { me: MeExpanded }): ReactElement {
  const fileInputId = useId();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const upload = useUploadAvatar();
  const remove = useDeleteAvatar();
  const setSession = useAuthStore((s) => s.setSession);
  const session = useAuthStore((s) => s.session);
  const qc = useQueryClient();
  const { toast } = useToast();
  const [confirmRemove, setConfirmRemove] = useState(false);

  function patchMeQueries(avatarUrl: string | null): void {
    // Update both query caches (`useMe` in @aeriosiq/auth-client and
    // `useExpandedMe` here) so the sidebar's avatar + the page's
    // avatar re-render against the same source of truth without
    // refetching.
    qc.setQueriesData<Record<string, unknown>>(
      { queryKey: ["auth", "me"] },
      (prev) => {
        if (!prev) return prev;
        return { ...prev, avatarUrl };
      },
    );
    qc.setQueriesData<Record<string, unknown>>(
      { queryKey: ["account", "me"] },
      (prev) => {
        if (!prev) return prev;
        return { ...prev, avatarUrl };
      },
    );
  }

  // Optimistic local mirror so AC7 / AC8 can assert on the rendered
  // image after a fetch resolves.
  const [optimisticAvatar, setOptimisticAvatar] = useState<string | null>(
    me.avatarUrl,
  );
  useEffect(() => {
    setOptimisticAvatar(me.avatarUrl);
  }, [me.avatarUrl]);

  async function onFileChosen(file: File) {
    try {
      const result = await upload.mutateAsync(file);
      setOptimisticAvatar(result.avatarUrl);
      patchMeQueries(result.avatarUrl);
      // Mirror the new URL into the auth store too — keeps reloads
      // (which rehydrate useMe.initialData from session.user) consistent
      // with the page's optimistic state.
      if (session) {
        setSession({
          ...session,
          user: { ...session.user, avatarUrl: result.avatarUrl },
        });
      }
      toast("Photo updated");
    } catch {
      toast("Couldn't upload photo", "error");
    }
  }

  async function onConfirmDelete() {
    try {
      await remove.mutateAsync();
      setOptimisticAvatar(null);
      patchMeQueries(null);
      if (session) {
        const userWithoutAvatar = { ...session.user };
        const u = userWithoutAvatar as unknown as Record<string, unknown>;
        delete u.avatarUrl;
        setSession({ ...session, user: userWithoutAvatar });
      }
      setConfirmRemove(false);
      toast("Photo removed");
    } catch {
      toast("Couldn't delete photo", "error");
    }
  }

  return (
    <Box>
      <Stack direction="row" spacing={3} alignItems="center">
        {optimisticAvatar ? (
          // SB2-W-14 — `<img>` with BOTH `alt` and `aria-label` gave
          // the element two competing accessible names; the WAI-ARIA
          // spec says `aria-label` wins and `alt` is silently dropped,
          // which axe-core flags as a duplicate-accessible-name
          // warning every render. Keep `alt` only (the standard
          // attribute for img alt-text) and drop the redundant
          // `aria-label`.
          <Box
            component="img"
            src={optimisticAvatar}
            alt="Profile photo"
            sx={{
              width: 96,
              height: 96,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
        ) : (
          <Avatar
            alt="Profile photo"
            sx={{ width: 96, height: 96, fontSize: 28 }}
          >
            {me.initials}
          </Avatar>
        )}
        <Stack spacing={1}>
          <Stack direction="row" spacing={1}>
            <input
              ref={fileRef}
              id={fileInputId}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              aria-label="Upload profile photo"
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0,0,0,0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  void onFileChosen(file);
                  // Reset so the same file can be chosen twice.
                  e.target.value = "";
                }
              }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<CameraIcon />}
              onClick={() => fileRef.current?.click()}
            >
              Upload photo
            </Button>
            {optimisticAvatar ? (
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<Trash2Icon />}
                onClick={() => {
                  setConfirmRemove(true);
                }}
              >
                Delete photo
              </Button>
            ) : null}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            JPEG, PNG, or WebP. Max 2 MB.
          </Typography>
        </Stack>
      </Stack>
      <Collapse in={confirmRemove}>
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                onClick={() => {
                  setConfirmRemove(false);
                }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => {
                  void onConfirmDelete();
                }}
              >
                Confirm
              </Button>
            </Stack>
          }
        >
          Remove your photo? You&apos;ll see your initials again.
        </Alert>
      </Collapse>
    </Box>
  );
}

function NameFields({ me }: { me: MeExpanded }): ReactElement {
  const update = useUpdateProfile();
  const { toast } = useToast();
  const [firstName, setFirstName] = useState(me.firstName ?? "");
  const [lastName, setLastName] = useState(me.lastName ?? "");
  const [nickName, setNickName] = useState(me.nickName ?? "");
  const [displayName, setDisplayName] = useState(me.displayName);

  useEffect(() => {
    setFirstName(me.firstName ?? "");
    setLastName(me.lastName ?? "");
    setNickName(me.nickName ?? "");
    setDisplayName(me.displayName);
  }, [me.firstName, me.lastName, me.nickName, me.displayName]);

  type NameField = "firstName" | "lastName" | "nickName" | "displayName";

  async function commit(field: NameField, value: string) {
    const initial: Record<NameField, string> = {
      firstName: me.firstName ?? "",
      lastName: me.lastName ?? "",
      nickName: me.nickName ?? "",
      displayName: me.displayName,
    };
    if (initial[field] === value) return;
    try {
      await update.mutateAsync({ [field]: value });
      toast("Saved");
    } catch {
      toast("Couldn't save changes", "error");
    }
  }

  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
      <AccountField
        label="First name"
        value={firstName}
        onChange={(e) => {
          setFirstName(e.target.value);
        }}
        onBlur={() => {
          void commit("firstName", firstName);
        }}
      />
      <AccountField
        label="Last name"
        value={lastName}
        onChange={(e) => {
          setLastName(e.target.value);
        }}
        onBlur={() => {
          void commit("lastName", lastName);
        }}
      />
      <AccountField
        label="Nickname"
        value={nickName}
        onChange={(e) => {
          setNickName(e.target.value);
        }}
        onBlur={() => {
          void commit("nickName", nickName);
        }}
      />
      <AccountField
        label="Display name"
        value={displayName}
        onChange={(e) => {
          setDisplayName(e.target.value);
        }}
        onBlur={() => {
          void commit("displayName", displayName);
        }}
      />
    </Stack>
  );
}

function PreferredLanguage({ me }: { me: MeExpanded }): ReactElement {
  const update = useUpdateProfile();
  const { toast } = useToast();
  const [lang, setLang] = useState<"en" | "de">(me.preferredLanguage);
  useEffect(() => {
    setLang(me.preferredLanguage);
  }, [me.preferredLanguage]);
  return (
    <AccountField
      label="Preferred language"
      select
      value={lang}
      onChange={(e) => {
        const next = e.target.value as "en" | "de";
        setLang(next);
        update.mutate(
          { preferredLanguage: next },
          {
            onSuccess: () => {
              toast("Saved");
            },
            onError: () => {
              toast("Couldn't save changes", "error");
            },
          },
        );
      }}
      sx={{ maxWidth: 280 }}
      fullWidth={false}
    >
      <MenuItem value="en">English</MenuItem>
      <MenuItem value="de">Deutsch</MenuItem>
    </AccountField>
  );
}

function CompanyRow({ me }: { me: MeExpanded }): ReactElement {
  // AC3: Company is NOT an input / textarea / select element. It must
  // expose an accessible name "Company" (via aria-label) on a non-input
  // tag — we use a <p> with aria-label.
  //
  // The live BE /me envelope omits the tenant block; fall back to the
  // host-resolved tenant in the auth store (set by useTenantFromHost
  // at boot).
  const storeTenant = useAuthStore((s) => s.tenant);
  const companyName = me.tenant?.displayName ?? storeTenant?.displayName ?? "—";
  return (
    <Stack spacing={0.5}>
      <Typography
        component="span"
        variant="caption"
        color="text.secondary"
        id="account-company-label"
      >
        Company
      </Typography>
      {/* SB2-W-14 — `aria-label` and `aria-labelledby` on the same
          element gave two competing accessible names; per WAI-ARIA
          the labelledby wins and the label is dropped, so axe-core
          flagged the conflict every render. Keep `aria-labelledby`
          only — it points at the visible "Company" caption above. */}
      <Typography
        component="p"
        aria-labelledby="account-company-label"
        sx={{ fontSize: "1rem", fontWeight: 500 }}
      >
        {companyName}
      </Typography>
    </Stack>
  );
}

function RoleRow({
  me,
  refetch,
}: {
  me: MeExpanded;
  refetch: () => Promise<unknown>;
}): ReactElement {
  const updateRole = useUpdateRole();
  const { toast } = useToast();
  // Story 074 — collapse via the ONE canonical resolver. This page used to
  // hand-roll its own precedence with tenant_admin checked FIRST, which
  // disagreed with `pickPrimaryRole` (most-privileged) everywhere else: a user
  // holding both platform_admin and tenant_admin saw "Tenant Admin" here while
  // the rest of the app treated them as platform_admin.
  //
  // `expert_analyst` stays the empty/unknown fallback — the BE-058 default for
  // a user with no explicit role mapping. The resolver returns `null` there and
  // deliberately leaves the display fallback to the caller.
  const primaryRole: Role = pickPrimaryRole(me.roles) ?? "expert_analyst";
  // Gate the editor off the SAME freshly-fetched `me` payload the label uses.
  // Deliberately not `useHasRole` here: that reads the session-derived context,
  // which this component exists to bypass (it renders from a fresh `/me`).
  const isTenantAdmin = primaryRoleIs(me.roles, "tenant_admin");
  const isLast = me.isLastTenantAdmin;

  const [draft, setDraft] = useState<Role>(primaryRole);
  useEffect(() => {
    setDraft(primaryRole);
  }, [primaryRole]);

  if (!isTenantAdmin) {
    // AC12: Analyst sees a read-only text label — no <select>, no <button>.
    return (
      <Stack spacing={0.5}>
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          id="account-role-label"
        >
          Role
        </Typography>
        {/* SB2-W-14 — see CompanyRow above; drop `aria-label` so the
            visible "Role" caption is the sole accessible name. */}
        <Typography
          component="p"
          aria-labelledby="account-role-label"
          sx={{ fontSize: "1rem", fontWeight: 500 }}
        >
          {ROLE_LABEL[primaryRole]}
        </Typography>
      </Stack>
    );
  }

  // AC13 / AC14: tenant_admin gets a select; if last admin, it's
  // aria-disabled + tooltip explains. We render a native <select> rather
  // than MUI's Select-as-listbox so Playwright's `selectOption` works
  // (the spec asserts on `<select>` semantics + aria-disabled).
  const disabled = isLast;
  const roleSelectId = "account-role-select";
  const select = (
    <Stack spacing={0.5} sx={{ maxWidth: 280 }}>
      <Box
        component="label"
        htmlFor={roleSelectId}
        sx={{
          fontSize: "0.75rem",
          color: "text.secondary",
          textTransform: "none",
        }}
      >
        Role
      </Box>
      <Box
        component="select"
        id={roleSelectId}
        aria-label="Role"
        aria-disabled={disabled ? "true" : "false"}
        value={draft}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          if (disabled) return;
          setDraft(e.target.value as Role);
        }}
        // Note: we deliberately do NOT set the `disabled` attribute
        // because AC14 hovers the element to surface the tooltip, and
        // pointer events on a real `disabled` select are blocked. The
        // `aria-disabled` semantics + the onChange guard are the
        // accessible disable signal.
        sx={(t) => ({
          width: "100%",
          minHeight: 40,
          px: 1.5,
          py: 1,
          fontSize: "1rem",
          color: disabled ? "text.disabled" : "text.primary",
          bgcolor: "background.paper",
          border: `1px solid ${t.palette.divider}`,
          borderRadius: 1,
          appearance: "auto",
          cursor: disabled ? "not-allowed" : "pointer",
          "&:focus-visible": {
            outline: `2px solid ${t.palette.primary.main}`,
            outlineOffset: 2,
          },
        })}
      >
        <option value="expert_analyst">Expert Analyst</option>
        <option value="pre_screener">Pre-Screener</option>
        <option value="project_manager">Project Manager</option>
        <option value="back_office">Back Office</option>
        <option value="pilot">Pilot</option>
        <option value="tenant_admin">Tenant Admin</option>
        {/* SB6-AUTH-PROFILE-01 — `platform_admin` is intentionally NOT an
            option here. It cannot be granted from inside a customer
            tenant; the BE would reject the PATCH (matrix-rule). The
            dedicated `apps/platform-admin` surface is the only place a
            platform-admin role can be assigned. Hiding it here removes
            the "I tried and got a vague error" footgun Christian hit on
            2026-05-24 [00:06:20]. If the user IS already platform_admin
            we still surface that label as their current role via
            ROLE_LABEL above; they just can't change AWAY from it from
            inside a tenant either (would need platform_admin tooling). */}
      </Box>
    </Stack>
  );

  return (
    <Stack spacing={1}>
      {disabled ? (
        <Tooltip
          title="You are the last Tenant Admin. Promote another user first."
          placement="right"
          arrow
        >
          <Box>{select}</Box>
        </Tooltip>
      ) : (
        select
      )}
      {!disabled && draft !== primaryRole ? (
        <Button
          variant="contained"
          size="small"
          sx={{ alignSelf: "flex-start" }}
          onClick={() => {
            updateRole.mutate(
              { role: draft },
              {
                onSuccess: async () => {
                  toast("Role updated");
                  await refetch();
                },
                onError: () => {
                  toast("Couldn't update role", "error");
                },
              },
            );
          }}
        >
          Save role
        </Button>
      ) : null}
      {disabled ? (
        <Typography variant="caption" color="text.secondary">
          You are the last Tenant Admin. Promote another user first.
        </Typography>
      ) : null}
    </Stack>
  );
}

function DetailsDisclosure({ me }: { me: MeExpanded }): ReactElement {
  // Always-visible details — spec AC2 requires both "Last login" and
  // "Password changed" to be visible on first paint via `getByText`.
  return (
    <Box>
      <Stack spacing={0.5} sx={{ mt: 1, pl: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Last login: {me.lastLoginAt ?? "Never"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Password changed on: {me.passwordChangedAt ?? "Never"}
        </Typography>
      </Stack>
    </Box>
  );
}
